import { useEffect, useRef, type ReactNode } from 'react';
import { Icon } from './Icon';

/**
 * The record you opened, beside the list you opened it from.
 *
 * On a wide screen it is a sticky pane on the right, so the list stays in view
 * and the next record is one click away. On a narrow one it follows the list,
 * and opening a record scrolls to it, because otherwise the click would appear
 * to do nothing.
 */
export function DetailPane({
  label,
  recordKey,
  title,
  subtitle,
  closeLabel,
  onClose,
  children,
}: {
  /** Names the region for a screen reader, and for a test. */
  label: string;
  /** Which record is open; a change scrolls the pane into view on a phone. */
  recordKey: string;
  title: ReactNode;
  subtitle?: ReactNode;
  closeLabel: string;
  onClose: () => void;
  children: ReactNode;
}): React.JSX.Element {
  const pane = useRef<HTMLElement>(null);

  useEffect(() => {
    if (window.matchMedia('(max-width: 68rem)').matches) {
      pane.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [recordKey]);

  return (
    <section className="detail" aria-label={label} ref={pane}>
      <header className="detail__head">
        <div className="detail__title">
          <h2>{title}</h2>
          {subtitle ? <p className="detail__subtitle">{subtitle}</p> : null}
        </div>
        <button type="button" className="icon-button" aria-label={closeLabel} title={closeLabel} onClick={onClose}>
          <Icon name="close" />
        </button>
      </header>
      <div className="detail__body">{children}</div>
    </section>
  );
}

/** The list, and the pane beside it once something is open. */
export function Split({ open, children }: { open: boolean; children: ReactNode }): React.JSX.Element {
  return <div className={`split${open ? ' split--open' : ''}`}>{children}</div>;
}

/** Grouped fields inside a detail pane, under a plain heading. */
export function DetailSection({ title, action, children }: { title: string; action?: ReactNode; children: ReactNode }): React.JSX.Element {
  return (
    <div className="detail__section">
      <div className="detail__section-head">
        <h3>{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}
